import { useState, useEffect, useCallback } from "react";
import BaseWrapper from "../../components/shared/BaseWrapper";
import { getCacheClients } from "../API/clientAPI";
import { BarChart } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  getInvoicesByClientIds,
  getAllInvoices,
  patchInvoiceItem,
} from "../API/billingAPI";
import { addMonthsToDateOnly, dateOnlyToDate, formatDateInPhilippines, todayInPhilippines } from "./utils/phDate";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

const CURRENCY = "PHP";
const PAGE_SIZE = 10;
const FETCH_LIMIT = 100;
const INVOICE_SEARCH_LIMIT = 100;
const STATUS_TABS = ["OVERDUE", "OPEN", "PAID"];

const fmt = (minor) =>
  `${CURRENCY} ${(Number(minor) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getStatusColor = (status) => {
  switch (status) {
    case "PAID":
      return "#155724";
    case "OVERDUE":
      return "#721c25";
    case "OPEN":
      return "#978d2d";
    default:
      return "#555";
  }
};

const getStatusBgColor = (status) => {
  switch (status) {
    case "PAID":
      return "#d4edda";
    case "OVERDUE":
      return "#f8d7da";
    case "OPEN":
      return "#fff9c4";
    default:
      return "#f5f5f5";
  }
};

const emptyMessageByStatus = {
  OVERDUE: "No overdue invoices today.",
  OPEN: "No open invoices found.",
  PAID: "No paid invoices found.",
};

const toIsoDate = (date) => {
  return formatDateInPhilippines(date) || "";
};

const addOneMonth = (dateString) => {
  if (!dateString) return "";

  return addMonthsToDateOnly(dateString, 1);
};

const isWithinOneMonth = (start, end) => {
  if (!start || !end) return true;

  const startDate = dateOnlyToDate(start);
  const endDate = dateOnlyToDate(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;

  const maxEnd = new Date(startDate);
  maxEnd.setMonth(maxEnd.getMonth() + 1);
  return endDate >= startDate && endDate <= maxEnd;
};

const isDateWithinRange = (value, start, end) => {
  if (!start) return true;
  if (!value) return false;

  const date = dateOnlyToDate(String(value).slice(0, 10));
  const startDate = dateOnlyToDate(start);
  const endDate = dateOnlyToDate(end || start);
  if (Number.isNaN(date.getTime()) || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return false;
  }

  return date >= startDate && date <= endDate;
};

const getInvoiceIdentifiers = (invoice) =>
  [invoice?.invoice_number, invoice?.invoice_id, invoice?.id]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());

function SalesReport() {
  const [clients, setClients] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState("OVERDUE");
  const [sortConfig, setSortConfig] = useState({ field: "due_date", dir: "asc" });
  const [markingPaid, setMarkingPaid] = useState(null);
  const [paidDialogInvoice, setPaidDialogInvoice] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState("");
  const [invoiceSearchMode, setInvoiceSearchMode] = useState(false);
  const [invoiceSearchLoading, setInvoiceSearchLoading] = useState(false);
  const [invoiceSearchMessage, setInvoiceSearchMessage] = useState("");
  const [paidStartDate, setPaidStartDate] = useState("");
  const [paidEndDate, setPaidEndDate] = useState("");
  const [page, setPage] = useState(0);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const paidDateRangeInvalid = activeTab === "PAID" && !isWithinOneMonth(paidStartDate, paidEndDate || paidStartDate);
  const paidDateRangeReady = invoiceSearchMode || activeTab !== "PAID" || Boolean(paidStartDate);
  const paidDateEnd = paidEndDate || paidStartDate;
  const filteredInvoices = allInvoices
    .filter((invoice) => invoice.status === activeTab)
    .filter((invoice) =>
      invoiceSearchMode ||
      activeTab !== "PAID" ||
      isDateWithinRange(invoice.payment_date, paidStartDate, paidDateEnd)
    );
  const paidRevenueTotal = activeTab === "PAID"
    ? filteredInvoices.reduce((sum, invoice) => sum + (Number(invoice.total_due_minor) || 0), 0)
    : 0;
  const balanceTotal = filteredInvoices.reduce((sum, invoice) => sum + (Number(invoice.total_due_minor) || 0), 0);
  const summaryLabel = activeTab === "PAID" ? "Total Revenue" : "Outstanding Balance";
  const summaryAmount = activeTab === "PAID" ? paidRevenueTotal : balanceTotal;

  const sortedInvoices = filteredInvoices
    .sort((a, b) => {
      const getSortTime = (invoice) => {
        const value = sortConfig.field === "payment_date" ? invoice.payment_date : invoice.due_date;
        const time = dateOnlyToDate(value)?.getTime();
        return Number.isFinite(time) ? time : null;
      };
      const aTime = getSortTime(a);
      const bTime = getSortTime(b);

      if (aTime === null && bTime === null) return 0;
      if (aTime === null) return 1;
      if (bTime === null) return -1;

      return sortConfig.dir === "asc" ? aTime - bTime : bTime - aTime;
    });

  const totalResultCount = sortedInvoices.length;
  const totalPages = Math.ceil(totalResultCount / PAGE_SIZE);
  const pagedInvoices = sortedInvoices.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const hasPrev = page > 0;
  const hasNextPage = page + 1 < totalPages;

  const clearInvoiceSearch = () => {
    if (!invoiceSearchMode && !invoiceSearchMessage) return;
    setInvoiceSearchMode(false);
    setInvoiceSearchMessage("");
  };

  const loadStatusInvoices = useCallback(async (status, query, clientList, signal, dateFilters = {}) => {
    const filters = { status, limit: FETCH_LIMIT, ...dateFilters };

    if (query) {
      const matchedClients = clientList.filter((client) =>
        (client.name || "").toLowerCase().includes(query)
      );

      if (matchedClients.length === 0) return [];

      return getInvoicesByClientIds(
        matchedClients.map((client) => client.id),
        filters,
        signal
      );
    }

    const invoices = [];
    let cursor = null;
    let guard = 0;

    do {
      const data = await getAllInvoices(
        {
          ...filters,
          ...(cursor ? { cursor } : {}),
        },
        signal
      );
      const items = data?.items ?? (Array.isArray(data) ? data : []);
      invoices.push(...items);
      cursor = data?.cursor ?? null;
      guard += 1;
    } while (cursor && guard < 50);

    return invoices;
  }, []);

  const findInvoiceByQuery = useCallback(async (query) => {
    const normalizedQuery = query.trim().toLowerCase();

    for (const status of STATUS_TABS) {
      let cursor = null;
      let guard = 0;

      do {
        const data = await getAllInvoices({
          status,
          limit: INVOICE_SEARCH_LIMIT,
          ...(cursor ? { cursor } : {}),
        });
        const invoices = data?.items ?? (Array.isArray(data) ? data : []);
        const matchedInvoice = invoices.find((invoice) =>
          getInvoiceIdentifiers(invoice).includes(normalizedQuery)
        );

        if (matchedInvoice) return matchedInvoice;

        cursor = data?.cursor ?? data?.next_cursor ?? null;
        guard += 1;
      } while (cursor && guard < 50);
    }

    return null;
  }, []);

  const handleOpenPaidDialog = (inv) => {
    setPaidDialogInvoice(inv);
    setPaymentNotes(inv.payment_notes || "");
  };

  const handleClosePaidDialog = () => {
    if (markingPaid) return;
    setPaidDialogInvoice(null);
    setPaymentNotes("");
  };

  const handleMarkAsPaid = async () => {
    const inv = paidDialogInvoice;
    if (!inv) return;

    const invoiceId = inv.invoice_id || inv.id;
    setMarkingPaid(invoiceId);
    try {
      const today = todayInPhilippines();
      const notes = paymentNotes.trim();
      const res = await patchInvoiceItem(invoiceId, {
        ...inv,
        status: "PAID",
        payment_date: today,
        payment_notes: notes,
        net_total_minor: Number(inv.net_total_minor) || 0,
        total_due_minor: Number(inv.total_due_minor) || 0,
        tax_minor: Number(inv.tax_minor) || 0,
        tax_rate_bps: Number(inv.tax_rate_bps) || 0,
      });

      if (res) {
        setAllInvoices((prev) =>
          prev.map((invoice) =>
            (invoice.invoice_id || invoice.id) === invoiceId
              ? { ...invoice, status: "PAID", payment_date: today, payment_notes: notes }
              : invoice
          )
        );
        setPaidDialogInvoice(null);
        setPaymentNotes("");
      }
    } catch (err) {
      console.error("Error marking invoice as paid:", err);
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleTabChange = (event, nextTab) => {
    clearInvoiceSearch();
    setActiveTab(nextTab);
    setSortConfig({ field: "due_date", dir: "asc" });
    setPage(0);
  };

  const handleSortClick = (field) => {
    setSortConfig((current) => ({
      field,
      dir: current.field === field && current.dir === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  const handleViewInvoice = (invoice) => {
    const invoiceId = invoice.invoice_id || invoice.id || Date.now();
    const storageKey = `salesReportInvoiceView:${invoiceId}:${Date.now()}`;
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        invoice,
        selectedClient: invoice.client || null,
        selectedContract: invoice.contract_id ? { contract_id: invoice.contract_id } : null,
        source: "salesReport",
      })
    );
    window.open(`/view-invoice?salesReportInvoice=${encodeURIComponent(storageKey)}`, "_blank");
  };

  const handleInvoiceSearch = async () => {
    const query = invoiceSearchQuery.trim();
    if (!query) {
      setInvoiceSearchMode(false);
      setInvoiceSearchMessage("");
      setPage(0);
      return;
    }

    setInvoiceSearchLoading(true);
    setInvoiceSearchMessage("");
    try {
      const matchedInvoice = await findInvoiceByQuery(query);
      if (matchedInvoice) {
        const matchedStatus = String(matchedInvoice.status || "").toUpperCase();
        setInvoiceSearchMode(true);
        setAllInvoices([matchedInvoice]);
        setActiveTab(STATUS_TABS.includes(matchedStatus) ? matchedStatus : "OPEN");
        setSortConfig({ field: "due_date", dir: "asc" });
        setPage(0);
      } else {
        setInvoiceSearchMode(true);
        setAllInvoices([]);
        setInvoiceSearchMessage(`No invoice found for "${query}".`);
        setPage(0);
      }
    } catch (err) {
      console.error("Error searching invoice:", err);
      setInvoiceSearchMode(true);
      setAllInvoices([]);
      setInvoiceSearchMessage("Unable to search invoices. Please try again.");
      setPage(0);
    } finally {
      setInvoiceSearchLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    const loadClients = async () => {
      setClientsLoading(true);
      try {
        const list = await getCacheClients();
        if (active) setClients(list || []);
      } catch {
        if (active) setClients([]);
      } finally {
        if (active) setClientsLoading(false);
      }
    };

    loadClients();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (invoiceSearchMode) return undefined;

    const controller = new AbortController();
    const query = searchQuery.trim().toLowerCase();
    const delay = query ? 300 : 0;
    const dateFilters = activeTab === "PAID" && paidStartDate
      ? {
          payment_start: paidStartDate,
          payment_end: paidDateEnd,
        }
      : {};

    setPage(0);

    const timer = setTimeout(async () => {
      if (!paidDateRangeReady || paidDateRangeInvalid) {
        setAllInvoices([]);
        return;
      }

      setInvoicesLoading(true);
      try {
        const invoices = await loadStatusInvoices(
          activeTab,
          query,
          clients,
          controller.signal,
          dateFilters
        );
        setAllInvoices(invoices);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("Error fetching sales report invoices:", err);
          setAllInvoices([]);
        }
      } finally {
        setInvoicesLoading(false);
      }
    }, delay);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [activeTab, searchQuery, clients, paidStartDate, paidDateEnd, paidDateRangeReady, paidDateRangeInvalid, loadStatusInvoices, invoiceSearchMode]);

  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [page, totalPages]);

  return (
    <BaseWrapper
      titleIcon={<BarChart />}
      title="Billing"
      subTitle="Sales Report"
      subTitleRoute="/sales-report"
    >
      <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search Client Name"
            value={searchQuery}
            onChange={(e) => {
              clearInvoiceSearch();
              setSearchQuery(e.target.value);
            }}
            sx={{ minWidth: 310 }}
          />
          {activeTab === "PAID" && !invoiceSearchMode && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="From"
                type="date"
                value={paidStartDate}
                sx={{ width: 170 }}
                onChange={(event) => {
                  const nextStart = event.target.value;
                  clearInvoiceSearch();
                  setPaidStartDate(nextStart);
                  if (paidEndDate && nextStart && !isWithinOneMonth(nextStart, paidEndDate)) {
                    setPaidEndDate("");
                  }
                  setPage(0);
                }}
                helperText=" "
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="To"
                type="date"
                value={paidEndDate}
                disabled={!paidStartDate}
                sx={{ width: 170 }}
                inputProps={{
                  min: paidStartDate || undefined,
                  max: addOneMonth(paidStartDate) || undefined,
                }}
                onChange={(event) => {
                  clearInvoiceSearch();
                  setPaidEndDate(event.target.value);
                  setPage(0);
                }}
                error={paidDateRangeInvalid}
                helperText={paidDateRangeInvalid ? "Limit the range to one month." : " "}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", ml: "auto" }}>
          <TextField
            size="small"
            placeholder="Search Invoice No."
            value={invoiceSearchQuery}
            onChange={(e) => setInvoiceSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleInvoiceSearch();
            }}
            sx={{ width: 230 }}
          />
          <Button
            size="medium"
            variant="contained"
            onClick={handleInvoiceSearch}
            disabled={invoiceSearchLoading}
          >
            {invoiceSearchLoading ? <CircularProgress size={18} color="inherit" /> : "Search"}
          </Button>
          {invoiceSearchMode && (
            <Button
              size="medium"
              variant="outlined"
              onClick={() => {
                setInvoiceSearchQuery("");
                setInvoiceSearchMode(false);
                setInvoiceSearchMessage("");
                setPage(0);
              }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>

      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: "1px solid #eee" }}
        >
          {STATUS_TABS.map((status) => (
            <Tab key={status} label={status} value={status} />
          ))}
        </Tabs>
      </Paper>

      {(clientsLoading || invoicesLoading) ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={1} sx={{ mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Client</TableCell>
                  <TableCell>Invoice No.</TableCell>
                  <TableCell>Billing Date</TableCell>
                  <TableCell sortDirection={sortConfig.field === "due_date" ? sortConfig.dir : false}>
                    <TableSortLabel
                      active={sortConfig.field === "due_date"}
                      direction={sortConfig.field === "due_date" ? sortConfig.dir : "asc"}
                      onClick={() => handleSortClick("due_date")}
                    >
                      Due Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortConfig.field === "payment_date" ? sortConfig.dir : false}>
                    <TableSortLabel
                      active={sortConfig.field === "payment_date"}
                      direction={sortConfig.field === "payment_date" ? sortConfig.dir : "asc"}
                      onClick={() => handleSortClick("payment_date")}
                    >
                      Payment Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Total Due</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedInvoices.map((inv) => (
                  <TableRow key={inv.invoice_id || inv.id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Tooltip title="View invoice">
                          <IconButton
                            size="small"
                            onClick={() => handleViewInvoice(inv)}
                            aria-label="View invoice"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Typography variant="body2">{inv.client?.name || "-"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{inv.invoice_number || inv.invoice_id}</TableCell>
                    <TableCell>{inv.billing_date || "-"}</TableCell>
                    <TableCell>{inv.due_date || "-"}</TableCell>
                    <TableCell>{inv.payment_date || "-"}</TableCell>
                    <TableCell align="right">{fmt(inv.total_due_minor)}</TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="caption"
                        sx={{
                          display: "inline-block",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: getStatusBgColor(inv.status),
                          color: getStatusColor(inv.status),
                          fontWeight: "bold",
                        }}
                      >
                        {inv.status}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {inv.status !== "PAID" && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled={markingPaid === (inv.invoice_id || inv.id)}
                          onClick={() => handleOpenPaidDialog(inv)}
                        >
                          {markingPaid === (inv.invoice_id || inv.id)
                            ? <CircularProgress size={16} />
                            : "Mark as Paid"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {pagedInvoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {activeTab === "PAID" && !paidStartDate
                          ? invoiceSearchMessage || "Select a date or date range to view paid invoices."
                          : invoiceSearchMessage ||
                            emptyMessageByStatus[activeTab]}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #eee" }}>
              <Typography variant="body2" color="text.secondary">
                Total {activeTab.toLowerCase()} results: {totalResultCount}
              </Typography>
            </Box>
          </TableContainer>

          {(activeTab !== "PAID" || (paidStartDate && !paidDateRangeInvalid)) && (
            <Paper elevation={1} sx={{ p: 2, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {summaryLabel}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#155724" }}>
                {fmt(summaryAmount)}
              </Typography>
            </Paper>
          )}

          {(hasPrev || hasNextPage) && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, py: 1 }}>
              <Button size="small" variant="outlined" disabled={!hasPrev} onClick={() => setPage((p) => p - 1)}>
                Back
              </Button>
              <Typography variant="body2" color="text.secondary">
                Page {page + 1} of {totalPages}
              </Typography>
              <Button size="small" variant="outlined" disabled={!hasNextPage} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={Boolean(paidDialogInvoice)} onClose={handleClosePaidDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Invoice as Paid</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure that you want to mark this invoice as paid? Please leave notes with the payment details.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Payment Notes"
            value={paymentNotes}
            onChange={(event) => setPaymentNotes(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaidDialog} disabled={Boolean(markingPaid)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkAsPaid}
            disabled={Boolean(markingPaid) || !paymentNotes.trim()}
          >
            {markingPaid ? <CircularProgress size={16} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </BaseWrapper>
  );
}

export default SalesReport;
